import { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Alert, Linking, Platform, Pressable, Text, View } from 'react-native';
import { setDoc, useDoc } from '../../store/store';
import { ActionSheet, Field, Section, type Action } from '../../components/ui';
import { C, alpha } from '../../components/theme';
import { PROVEDORES_IA, avisarChave, conferirChave, limparChave, listarModelos, nomeIA, testarChave, textoErroIA, type ProvedorIA } from '../../lib/ia';
import { lerChave, salvarChave } from '../../lib/iaChave';
import { semBloqueio } from '../../auth/AuthContext';
import { t } from '../../lib/i18n';
import { Card } from './comum';
import { BotaoAcao, Bloco, LinhaChave, LinhaEscolha } from '../../components/ajustes/Linhas';

/** Ajustes > Inteligência artificial: liga o Claude, o ChatGPT ou o Gemini do próprio usuário (chave de API). */
export default function IA({ cor }: { cor: string }) {
  const aj = useDoc('ajustes');
  const cfg = aj.ia ?? { provedor: null, modelos: {}, auto: true };
  const [escolhido, setEscolhido] = useState<ProvedorIA>(cfg.provedor ?? 'gemini');
  const [temChave, setTemChave] = useState<Record<string, boolean>>({});
  const [chave, setChave] = useState('');
  const [modelos, setModelos] = useState<string[]>([]);
  const [ocupado, setOcupado] = useState(false);
  const info = PROVEDORES_IA.find(p => p.id === escolhido)!;
  const salvarCfg = (fn: (c: typeof cfg) => typeof cfg) => setDoc('ajustes', d => ({ ...d, ia: fn(d.ia ?? cfg) }));

  useEffect(() => {
    Promise.all(PROVEDORES_IA.map(async p => [p.id, !!(await lerChave(p.id))] as const)).then(l => setTemChave(Object.fromEntries(l)));
  }, []);
  const chavePendente = useRef('');   // chave colada no serviço errado: passa para o serviço certo ao trocar
  useEffect(() => { setChave(chavePendente.current); chavePendente.current = ''; setModelos([]); }, [escolhido]);

  async function testar(colada: string) {
    // 0.72: limpa a chave colada (espaços, quebras de linha, aspas, invisíveis) e confere o formato antes de chamar a IA
    const k = limparChave(colada);
    const prob = conferirChave(escolhido, k);
    if (prob) {
      const destino = prob.trocarPara;
      return Alert.alert(t('Confira a chave'), prob.erro, destino
        ? [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Usar no {ia}', { ia: nomeIA(destino) }), onPress: () => { chavePendente.current = k; setEscolhido(destino); } }]
        : undefined);
    }
    setOcupado(true);
    try {
      const r = await testarChave(escolhido, k);
      await salvarChave(escolhido, k);
      avisarChave();
      setTemChave(x => ({ ...x, [escolhido]: true }));
      setModelos(r.modelos.slice(0, 30));
      salvarCfg(c => ({ ...c, provedor: escolhido, modelos: { ...c.modelos, [escolhido]: r.modelo } }));
      setChave('');
      Alert.alert(t('IA ligada'), t('{ia} respondeu ao teste com o modelo {modelo}. Já pode usar nas abas.', { ia: info.nome, modelo: r.modelo }));
    } catch (e: any) { Alert.alert(t('Não deu certo'), textoErroIA(e)); }
    finally { setOcupado(false); }
  }
  async function verModelos(): Promise<string[]> {
    const k = limparChave((await lerChave(escolhido)) ?? '');
    if (!k) return [];
    setOcupado(true);
    try { const l = (await listarModelos(escolhido, k)).slice(0, 30); setModelos(l); return l; } catch (e: any) { Alert.alert(t('Não deu certo'), textoErroIA(e)); return []; }
    finally { setOcupado(false); }
  }
  const modeloAtual = cfg.modelos[escolhido] || info.padrao;
  const [menu, setMenu] = useState<{ titulo: string; acoes: Action[] } | null>(null);
  const [verPassos, setVerPassos] = useState(false);
  const abrirProvedores = () => setMenu({ titulo: t('Qual IA'), acoes: PROVEDORES_IA.map(p => ({
    label: `${p.id === escolhido ? '✓ ' : '    '}${p.nome}${temChave[p.id] ? ' 🔑' : ''}`, onPress: () => setEscolhido(p.id) })) });
  async function abrirModelos() {
    const lista = modelos.length ? modelos : await verModelos();
    if (!lista.length) return;
    const todos = lista.includes(modeloAtual) ? lista : [modeloAtual, ...lista];
    setMenu({ titulo: t('Modelo'), acoes: todos.map(m => ({ label: (m === modeloAtual ? '✓ ' : '    ') + m,
      onPress: () => salvarCfg(c => ({ ...c, modelos: { ...c.modelos, [escolhido]: m } })) })) });
  }

  return (<>
    <Text style={{ color: C.sub }}>{t('Use a sua própria IA para ler publicações, documentos e anotações e sugerir agendamentos. Você sempre confirma antes de qualquer coisa entrar no Planner.')}</Text>
    <Card>
      <Text style={{ color: C.warn, fontSize: 12 }}>{t('As assinaturas Claude Pro, ChatGPT Plus e Gemini Advanced não podem ser usadas por outros apps. Para ligar a IA aqui, crie uma chave de API na sua conta (o Gemini tem uso gratuito; Claude e ChatGPT cobram por uso, centavos por análise).')}</Text>
    </Card>

    <Bloco>
      <LinhaEscolha icone="🤖" titulo={t('Qual IA')} valor={`${info.nome}${temChave[escolhido] ? ' 🔑' : ''}`} onPress={abrirProvedores} />
    </Bloco>

    <Card>
      {cfg.provedor === escolhido && temChave[escolhido]
        ? <Text style={{ color: C.ok, fontWeight: '700' }}>✓ {t('Em uso: {ia}', { ia: info.nome })}</Text>
        : temChave[escolhido]
          ? <BotaoAcao cheio cor={cor} label={t('Usar {ia}', { ia: info.nome })} onPress={() => salvarCfg(c => ({ ...c, provedor: escolhido }))} />
          : <Text style={{ color: C.sub }}>{t('Ainda sem chave neste dispositivo.')}</Text>}
      <View>
        <LinhaEscolha icone="🔑" cor={cor} titulo={t('Criar minha chave de API') + ' ↗'} onPress={() => { semBloqueio(() => Linking.openURL(info.site)).catch(() => {}); }} />
        <Text style={{ color: C.mute, fontSize: 11, marginTop: -6, marginLeft: 28 }} numberOfLines={1}>{info.site}</Text>
      </View>
      <Pressable onPress={() => setVerPassos(v => !v)} hitSlop={6}>
        <Text style={{ color: cor, fontWeight: '700' }}>{(verPassos ? '▾ ' : '▸ ') + t('Passo a passo para criar a chave')}</Text>
      </Pressable>
      {verPassos && <PassoAPasso p={escolhido} cor={cor} />}
      <Field value={chave} onChangeText={setChave} placeholder={t('Cole aqui a chave ({ini}...)', { ini: info.chave })} secureTextEntry autoCapitalize="none" autoCorrect={false} />
      <BotaoAcao cheio cor={cor} label={ocupado ? t('Aguarde...') : t('Salvar e testar')} disabled={ocupado || !limparChave(chave)} onPress={() => testar(chave)} />
      {temChave[escolhido] && (<>
        <LinhaEscolha icone="🧠" titulo={t('Modelo')} valor={modeloAtual} onPress={abrirModelos} />
        {ocupado && <ActivityIndicator color={cor} />}
        <BotaoAcao cor={C.danger} label={t('Apagar a chave')} onPress={() => Alert.alert(t('Apagar a chave deste dispositivo?'), '', [{ text: t('Cancelar'), style: 'cancel' },
          { text: t('Apagar'), style: 'destructive', onPress: async () => {
            await salvarChave(escolhido, null); avisarChave(); setTemChave(x => ({ ...x, [escolhido]: false }));
            if (cfg.provedor === escolhido) salvarCfg(c => ({ ...c, provedor: null }));
          } }])} />
      </>)}
      <Text style={{ color: C.mute, fontSize: 11 }}>
        {Platform.OS === 'web'
          ? t('A chave fica guardada só neste navegador. Use num computador pessoal.')
          : t('A chave fica guardada só neste dispositivo, no cofre do sistema. Em outro dispositivo, cadastre de novo.')}
      </Text>
    </Card>

    <Section t={t('Uso')} />
    <Card>
      <LinhaChave icone="📰" titulo={t('Ler sozinho as publicações novas')} cor={cor} valor={cfg.auto} onMudar={v => salvarCfg(c => ({ ...c, auto: v }))} />
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('Onde a IA ajuda: Publicações (audiências e prazos), Documentos (preencher a ficha pela foto) e Anotações (transformar uma caixa em tarefas).')}</Text>
      <View style={{ backgroundColor: alpha(cor, 0.1), borderRadius: 10, padding: 10 }}>
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Privacidade: o texto ou a imagem analisada vai direto deste dispositivo para a empresa da IA escolhida, com a sua chave. O My Planner não guarda cópia.')}</Text>
      </View>
    </Card>
    <ActionSheet title={menu?.titulo ?? ''} actions={menu?.acoes ?? null} onClose={() => setMenu(null)} />
  </>);
}

/** Passo a passo curto para criar a chave de cada IA (0.72). */
function PassoAPasso({ p, cor }: { p: ProvedorIA; cor: string }) {
  const passos: string[] = p === 'gemini' ? [
    '🌐 ' + t('Toque em "Criar minha chave de API" acima. Abre o Google AI Studio.'),
    '👤 ' + t('Entre com a sua conta Google (a mesma do Gmail serve).'),
    '📜 ' + t('Na primeira vez, aceite os termos de uso.'),
    '🔑 ' + t('Toque em "Create API key" (Criar chave de API). Se pedir um projeto, escolha o que aparecer ou crie um novo.'),
    '📋 ' + t('Toque no ícone de copiar ao lado da chave. Ela começa com "AIza" ou "AQ.".'),
    '↩️ ' + t('Volte aqui, cole no campo abaixo e toque em "Salvar e testar".'),
  ] : p === 'openai' ? [
    '🌐 ' + t('Toque em "Criar minha chave de API" acima. Abre a plataforma da OpenAI (não é o site do ChatGPT).'),
    '👤 ' + t('Entre com a mesma conta do ChatGPT ou crie uma.'),
    '💳 ' + t('Em "Billing" (Cobrança), adicione um cartão e alguns dólares de crédito. A assinatura ChatGPT Plus não vale aqui.'),
    '🔑 ' + t('Volte em "API keys" e toque em "Create new secret key" (Criar chave).'),
    '📋 ' + t('Copie a chave na hora (ela só aparece uma vez). Começa com "sk-".'),
    '↩️ ' + t('Volte aqui, cole no campo abaixo e toque em "Salvar e testar".'),
  ] : [
    '🌐 ' + t('Toque em "Criar minha chave de API" acima. Abre o Console da Anthropic (não é o site do Claude).'),
    '👤 ' + t('Entre ou crie uma conta (pode ser o mesmo e-mail do Claude).'),
    '💳 ' + t('Em "Billing" (Cobrança), compre créditos. A assinatura Claude Pro não vale aqui.'),
    '🔑 ' + t('Em "API keys", toque em "Create key" (Criar chave) e dê um nome, por exemplo "My Planner".'),
    '📋 ' + t('Copie a chave na hora (ela só aparece uma vez). Começa com "sk-ant-".'),
    '↩️ ' + t('Volte aqui, cole no campo abaixo e toque em "Salvar e testar".'),
  ];
  return (
    <View style={{ backgroundColor: alpha(cor, 0.08), borderRadius: 10, padding: 10, gap: 8 }}>
      {passos.map((x, i) => (
        <View key={i} style={{ flexDirection: 'row', gap: 8 }}>
          <Text style={{ color: cor, fontWeight: '800', width: 18 }}>{i + 1}.</Text>
          <Text style={{ color: C.text, fontSize: 13, flex: 1 }}>{x}</Text>
        </View>
      ))}
      <Text style={{ color: C.mute, fontSize: 11 }}>{t('Dica: a chave é como uma senha. Não mande por mensagem nem publique. Se vazar, apague-a no site e crie outra.')}</Text>
    </View>
  );
}
